import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './css/ListView.css';
import {
  Modal,
  Button,
  Table,
  Form,
  Badge,
  Spinner,
  Alert,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { FaUndo } from 'react-icons/fa';
import moment from 'moment-timezone';
import LookupLayout, { isOwnerUserLookupField } from './LookupLayout';
import MultiSelectDropdown from './MultiSelectDropdown1';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import DataApi from '../../api/DataApi';
import { getUserPreferences } from '../../utils/formatUtils';
import { isFieldValueMasked } from '../../utils/fieldConfigUtils';
import { getFlsEditDeniedLowerSet, getUserFieldPermissionsByObject } from '../../utils/permissionCache';
import { getApiErrorResponseData } from '../../utils/apiErrorMessage';

const REQUIRED_INDICATOR_COLOR = '#ff5d5d';
const REQUIRED_ASTERISK_STYLE = {
  color: REQUIRED_INDICATOR_COLOR,
  marginRight: 4,
  fontWeight: 600,
  lineHeight: 1,
};
const REQUIRED_INPUT_STYLE = {
  boxShadow: `inset 3px 0 0 0 ${REQUIRED_INDICATOR_COLOR}`,
  borderColor: '#ced4da',
};
const CELL_INPUT_STYLE = {
  minWidth: 140,
  fontSize: '0.875rem',
};

const BLOCKED_FIELD_NAMES = new Set([
  'id',
  'ownerid',
  'createddate',
  'createdbyid',
  'lastmodifieddate',
  'lastmodifiedbyid',
  'is_deleted',
  'recordviewid',
  'recordview_id',
]);

/** Never shown as columns in the bulk-edit grid */
const HIDDEN_COLUMN_TYPES = new Set([
  'system',
  'image',
  'barcode',
  'qrcode',
]);

/**
 * Shown in the grid as read-only (not editable / not included in save payload).
 * Calculated + rollup must remain visible when present on the list view.
 */
const READONLY_DISPLAY_TYPES = new Set([
  'calculated',
  'rollupsummary',
  'autonumber',
]);

const normalizeBoolean = value => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    return lower === 'true' || lower === '1' || lower === 'yes';
  }
  if (typeof value === 'number') return value === 1;
  return false;
};

const formatDateValue = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    return '';
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateTimeValue = value => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    return value.slice(0, 16);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${hh}:${mm}`;
};

const formatTimeValue = value => {
  if (!value) return '';
  if (typeof value === 'string' && value.includes('T')) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    }
  }
  if (typeof value === 'string' && value.includes(':')) {
    const parts = value.split(':');
    return `${parts[0]}:${parts[1]}`;
  }
  return String(value);
};

const valuesEqual = (a, b) => {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return normalizeBoolean(a) === normalizeBoolean(b);
  }
  return String(a) === String(b);
};

const getPicklistOptions = meta => {
  const raw =
    meta?.picklist_values_data ||
    meta?.picklist_values ||
    meta?.picklistValues ||
    meta?.picklist_values_json ||
    [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map(option => ({
      key: String(option.key || option.value_key || option.value || ''),
      value: String(
        option.value || option.value_label || option.label || option.key || ''
      ),
    }))
    .filter(o => o.key);
};

const getRecordIdFromListRecord = record => {
  if (!record) return null;
  if (record.__recordId != null && record.__recordId !== '') {
    return String(record.__recordId);
  }
  if (record.id && typeof record.id === 'object' && record.id.value != null) {
    return String(record.id.value);
  }
  if (record.id != null && typeof record.id !== 'object') {
    return String(record.id);
  }
  return null;
};

/** Case-insensitive field lookup on a list-view record. */
const getRecordField = (record, fieldName) => {
  if (!record || fieldName == null || fieldName === '') return undefined;
  if (Object.prototype.hasOwnProperty.call(record, fieldName)) {
    return record[fieldName];
  }
  const want = String(fieldName).toLowerCase();
  const key = Object.keys(record).find(k => String(k).toLowerCase() === want);
  return key != null ? record[key] : undefined;
};

const TEXTUAL_TYPES = new Set([
  'text',
  'string',
  'textarea',
  'richtext',
  'email',
  'phone',
  'url',
  'autonumber',
]);

/**
 * Prefer storage value; for simple text fields fall back to displayValue
 * (list payloads sometimes hydrate display first on initial render).
 */
const extractCellValue = (field, dataType = '') => {
  if (field == null) return '';
  if (typeof field !== 'object' || Array.isArray(field)) return field;
  const raw = field.value;
  const hasRaw =
    raw !== undefined && raw !== null && !(typeof raw === 'string' && raw.trim() === '');
  if (hasRaw) return raw;
  const typeLc = String(dataType || field.type || '').toLowerCase();
  const display = field.displayValue;
  if (
    display != null &&
    String(display).trim() !== '' &&
    (TEXTUAL_TYPES.has(typeLc) ||
      READONLY_DISPLAY_TYPES.has(typeLc) ||
      !typeLc)
  ) {
    return display;
  }
  if (raw !== undefined) return raw ?? '';
  return '';
};

const parseObjectConfig = raw => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  return typeof raw === 'object' ? raw : {};
};

/** Resolve object_config.config.name_field (default name / link field). */
const resolveNameFieldKey = (objectConfig, availableFields = []) => {
  const cfg = parseObjectConfig(objectConfig);
  const configured = String(cfg.name_field ?? cfg.nameField ?? '')
    .trim()
    .toLowerCase();
  const available = new Set(
    (availableFields || []).map(f => String(f || '').toLowerCase())
  );
  if (configured && (available.size === 0 || available.has(configured))) {
    return configured;
  }
  const fallbacks = [
    'name',
    'lastname',
    'firstname',
    'subject',
    'title',
    'email',
    'company',
  ];
  for (const key of fallbacks) {
    if (available.size === 0 || available.has(key)) return key;
  }
  return configured || 'name';
};

const pickLabelFromRecord = (record, nameFieldKey, values = {}, displayValues = {}) => {
  const tryKeys = [
    nameFieldKey,
    'name',
    'lastname',
    'firstname',
    'subject',
    'title',
    'email',
  ].filter(Boolean);

  const seen = new Set();
  for (const key of tryKeys) {
    const k = String(key).toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    const cell = getRecordField(record, key);
    const fromDisplay =
      displayValues[key] ??
      displayValues[k] ??
      extractCellDisplay(cell);
    if (fromDisplay != null && String(fromDisplay).trim() !== '') {
      return String(fromDisplay).trim();
    }
    const fromValue =
      values[key] ??
      values[k] ??
      extractCellValue(cell);
    if (fromValue != null && String(fromValue).trim() !== '') {
      return String(fromValue).trim();
    }
  }

  // Contact-style: firstname + lastname
  const firstCell = getRecordField(record, 'firstname');
  const lastCell = getRecordField(record, 'lastname');
  const first =
    displayValues.firstname ||
    values.firstname ||
    extractCellDisplay(firstCell) ||
    extractCellValue(firstCell) ||
    '';
  const last =
    displayValues.lastname ||
    values.lastname ||
    extractCellDisplay(lastCell) ||
    extractCellValue(lastCell) ||
    '';
  const combined = `${first} ${last}`.trim();
  return combined || '';
};

const extractCellDisplay = field => {
  if (field == null || typeof field !== 'object' || Array.isArray(field)) return '';
  if (field.displayValue != null && field.displayValue !== '') {
    return String(field.displayValue);
  }
  if (field.objectLabel) return String(field.objectLabel);
  if (field.value != null && field.value !== '' && typeof field.value !== 'object') {
    return String(field.value);
  }
  return '';
};

function sanitizeFieldValue(dataType, value) {
  const t = String(dataType || '').toLowerCase();
  if (t === 'datetime' && value) {
    const prefs = getUserPreferences();
    const userTimezone = prefs.time_zone || 'UTC';
    return moment.tz(value, userTimezone).format();
  }
  if (t === 'date' && value) {
    const prefs = getUserPreferences();
    const userTimezone = prefs.time_zone || 'UTC';
    return moment.tz(value, userTimezone).startOf('day').format();
  }
  if (t === 'time' && value) {
    let timeValue = value;
    if (typeof timeValue === 'string' && timeValue.includes(':')) {
      const parts = timeValue.split(':');
      if (parts.length === 2) timeValue = `${timeValue}:00`;
    }
    return timeValue;
  }
  if (['number', 'decimal', 'float', 'currency', 'percent', 'double', 'int', 'integer'].includes(t)) {
    if (value === '' || value == null) return null;
    return Number(value);
  }
  if (t === 'boolean' || t === 'checkbox') {
    return Boolean(value);
  }
  if (value === '') return null;
  return value ?? null;
}

/**
 * List-view bulk edit modal: Excel-like grid of selected records × list-view columns.
 * Each dirty record is saved separately via DataApi.update (full datatype-aware cell editors).
 */
export default function BulkEditLayout({
  show = false,
  onHide = () => {},
  onClose = () => {},
  objectName,
  objectLabel,
  /** List-view column field names (order preserved) */
  fieldNames = [],
  /** Prepared list-view records (selected rows) */
  selectedRecords = [],
}) {
  const [rows, setRows] = useState([]);
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [objectConfig, setObjectConfig] = useState({});
  const [flsNoEdit, setFlsNoEdit] = useState(() => new Set());
  const [configLoading, setConfigLoading] = useState(false);
  const [rowsReady, setRowsReady] = useState(false);
  const [configError, setConfigError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [apiError, setApiError] = useState('');
  /** Snapshot records/columns when modal opens so first paint isn't raced by parent re-renders */
  const openSnapshotRef = useRef({ records: [], columns: [] });

  const nameFieldKey = useMemo(
    () => resolveNameFieldKey(objectConfig, fieldNames),
    [objectConfig, fieldNames]
  );

  const objectFieldsMap = useMemo(() => {
    const map = {};
    (fieldConfigs || []).forEach(field => {
      if (field?.field_name) {
        map[field.field_name] = field;
        map[String(field.field_name).toLowerCase()] = field;
      }
    });
    return map;
  }, [fieldConfigs]);

  const getFieldMeta = useCallback(
    fieldName => {
      if (!fieldName) return {};
      return (
        objectFieldsMap[fieldName] ||
        objectFieldsMap[String(fieldName).toLowerCase()] ||
        {}
      );
    },
    [objectFieldsMap]
  );

  const isFieldEditable = useCallback(
    fieldName => {
      if (!fieldName) return false;
      const nameLc = String(fieldName).toLowerCase();
      if (BLOCKED_FIELD_NAMES.has(nameLc)) return false;
      if (flsNoEdit.has(nameLc)) return false;
      const meta = getFieldMeta(fieldName);
      const dataType = String(meta.data_type || meta.type || '').toLowerCase();
      if (READONLY_DISPLAY_TYPES.has(dataType)) return false;
      if (HIDDEN_COLUMN_TYPES.has(dataType)) return false;
      if (
        meta.is_readonly === true ||
        meta.isReadonly === true ||
        meta.readonly === true ||
        meta.read_only === true
      ) {
        return false;
      }
      if (isFieldValueMasked(meta)) return false;
      if (dataType === 'password') return false;
      return true;
    },
    [flsNoEdit, getFieldMeta]
  );

  /** Columns to show: editable fields + calculated/rollup (read-only). */
  const isColumnVisible = useCallback(
    fieldName => {
      if (!fieldName || String(fieldName).toLowerCase() === 'id') return false;
      const nameLc = String(fieldName).toLowerCase();
      if (BLOCKED_FIELD_NAMES.has(nameLc)) return false;
      if (Object.keys(objectFieldsMap).length === 0) return true;
      const meta = getFieldMeta(fieldName);
      const dataType = String(meta.data_type || meta.type || '').toLowerCase();
      if (HIDDEN_COLUMN_TYPES.has(dataType)) return false;
      if (dataType === 'password' || isFieldValueMasked(meta)) return false;
      // Always show calculated / rollup / autonumber as read-only when on the list view
      if (READONLY_DISPLAY_TYPES.has(dataType)) return true;
      if (flsNoEdit.has(nameLc)) return false;
      if (
        meta.is_readonly === true ||
        meta.isReadonly === true ||
        meta.readonly === true ||
        meta.read_only === true
      ) {
        return false;
      }
      return true;
    },
    [flsNoEdit, getFieldMeta, objectFieldsMap]
  );

  const activeFieldNames = useMemo(() => {
    const names = Array.isArray(fieldNames) ? fieldNames : [];
    return names.filter(name => isColumnVisible(name));
  }, [fieldNames, isColumnVisible]);

  const buildRowsFromRecords = useCallback(
    (records, columns, nameKey, fieldsMap = {}) => {
      return (records || [])
        .map(record => {
          const recordId = getRecordIdFromListRecord(record);
          if (!recordId) return null;
          const values = {};
          const displayValues = {};
          const fillField = fieldName => {
            if (!fieldName || values[fieldName] !== undefined) return;
            const cell = getRecordField(record, fieldName);
            const meta =
              fieldsMap[fieldName] ||
              fieldsMap[String(fieldName).toLowerCase()] ||
              {};
            const dataType = meta.data_type || meta.type || cell?.type || '';
            values[fieldName] = extractCellValue(cell, dataType);
            const display = extractCellDisplay(cell);
            if (display) displayValues[fieldName] = display;
          };
          (columns || []).forEach(fillField);
          // Also capture name-field cells even if not in list columns (for error labels)
          [nameKey, 'name', 'lastname', 'firstname'].forEach(fillField);
          const recordLabel = pickLabelFromRecord(
            record,
            nameKey,
            values,
            displayValues
          );
          return {
            key: recordId,
            recordId,
            recordLabel: recordLabel || recordId,
            values,
            originalValues: { ...values },
            displayValues,
            error: null,
          };
        })
        .filter(Boolean);
    },
    []
  );

  // Open modal: snapshot selection, load config, THEN build rows once (avoids blank first-name race)
  useEffect(() => {
    if (!show) {
      setRowsReady(false);
      setConfigLoading(false);
      return undefined;
    }
    if (!objectName) return undefined;

    let cancelled = false;
    const snapshotRecords = Array.isArray(selectedRecords)
      ? selectedRecords.map(r => ({ ...r }))
      : [];
    const snapshotColumns = Array.isArray(fieldNames) ? [...fieldNames] : [];
    openSnapshotRef.current = {
      records: snapshotRecords,
      columns: snapshotColumns,
    };

    setApiError('');
    setSaveResult(null);
    setConfigError('');
    setRows([]);
    setRowsReady(false);
    setConfigLoading(true);

    (async () => {
      try {
        const [fieldResponse, flsByObj] = await Promise.all([
          ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName),
          getUserFieldPermissionsByObject().catch(() => null),
        ]);
        if (cancelled) return;

        const result = fieldResponse?.data;
        const fields = Array.isArray(result?.fields) ? result.fields : [];
        const cfg = parseObjectConfig(result?.config);
        const flsDenied = getFlsEditDeniedLowerSet(flsByObj, objectName);

        const fieldsMap = {};
        fields.forEach(field => {
          if (field?.field_name) {
            fieldsMap[field.field_name] = field;
            fieldsMap[String(field.field_name).toLowerCase()] = field;
          }
        });

        const nameKey = resolveNameFieldKey(cfg, snapshotColumns);
        const visibleColumns = snapshotColumns.filter(name => {
          if (!name || String(name).toLowerCase() === 'id') return false;
          const nameLc = String(name).toLowerCase();
          if (BLOCKED_FIELD_NAMES.has(nameLc)) return false;
          const meta = fieldsMap[name] || fieldsMap[nameLc] || {};
          const dataType = String(meta.data_type || meta.type || '').toLowerCase();
          if (HIDDEN_COLUMN_TYPES.has(dataType)) return false;
          if (isFieldValueMasked(meta) || dataType === 'password') return false;
          // Calculated / rollup / autonumber: keep visible as read-only
          if (READONLY_DISPLAY_TYPES.has(dataType)) return true;
          if (flsDenied.has(nameLc)) return false;
          if (
            meta.is_readonly === true ||
            meta.isReadonly === true ||
            meta.readonly === true ||
            meta.read_only === true
          ) {
            return false;
          }
          return true;
        });

        const built = buildRowsFromRecords(
          snapshotRecords,
          visibleColumns.length > 0 ? visibleColumns : snapshotColumns,
          nameKey,
          fieldsMap
        );

        if (cancelled) return;
        setFieldConfigs(fields);
        setObjectConfig(cfg);
        setFlsNoEdit(flsDenied);
        setRows(built);
        setRowsReady(true);
      } catch (e) {
        if (!cancelled) {
          console.error('BulkEditLayout: failed to load field config', e);
          setConfigError(e?.message || 'Failed to load field configuration.');
          setFieldConfigs([]);
          setObjectConfig({});
          setRows([]);
          setRowsReady(true);
        }
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Only re-run when modal opens / object changes — snapshot captures selection at open time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, objectName]);

  const isBusy = configLoading || saving || (show && !rowsReady && !configError);

  const dirtyRowCount = useMemo(
    () =>
      rows.filter(row =>
        activeFieldNames.some(
          fn => !valuesEqual(row.values?.[fn], row.originalValues?.[fn])
        )
      ).length,
    [rows, activeFieldNames]
  );

  const refreshRowLabel = (row, nextValues, nextDisplayValues) => {
    const label = pickLabelFromRecord(
      null,
      nameFieldKey,
      nextValues,
      nextDisplayValues
    );
    return label || row.recordLabel || row.recordId;
  };

  const handleFieldChange = (rowKey, fieldName, newValue) => {
    setRows(prev =>
      prev.map(row => {
        if (row.key !== rowKey) return row;
        const values = { ...row.values, [fieldName]: newValue };
        return {
          ...row,
          error: null,
          values,
          recordLabel: refreshRowLabel(row, values, row.displayValues),
        };
      })
    );
  };

  const handleLookupChange = (rowKey, fieldName, newId, displayValue) => {
    setRows(prev =>
      prev.map(row => {
        if (row.key !== rowKey) return row;
        const values = { ...row.values, [fieldName]: newId };
        const displayValues = {
          ...row.displayValues,
          [fieldName]: displayValue,
        };
        return {
          ...row,
          error: null,
          values,
          displayValues,
          recordLabel: refreshRowLabel(row, values, displayValues),
        };
      })
    );
  };

  const handleResetRow = rowKey => {
    setRows(prev =>
      prev.map(row => {
        if (row.key !== rowKey) return row;
        return {
          ...row,
          error: null,
          values: { ...row.originalValues },
        };
      })
    );
  };

  const buildDirtyPayload = row => {
    const payload = {};
    activeFieldNames.forEach(fieldName => {
      if (!isFieldEditable(fieldName)) return;
      const next = row.values?.[fieldName];
      const prev = row.originalValues?.[fieldName];
      if (valuesEqual(next, prev)) return;
      const meta = objectFieldsMap[fieldName] || {};
      const dataType = meta.data_type || meta.type || 'text';
      payload[fieldName] = sanitizeFieldValue(dataType, next);
    });
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    setApiError('');
    setSaveResult(null);

    const dirtyRows = rows.filter(
      row => Object.keys(buildDirtyPayload(row)).length > 0
    );

    if (dirtyRows.length === 0) {
      setApiError('No changes to save.');
      setSaving(false);
      return;
    }

    const dataApi = new DataApi(`object/${objectName}`);
    const result = {
      total: dirtyRows.length,
      updated: 0,
      failed: 0,
      errors: [],
    };

    for (const row of dirtyRows) {
      const payload = buildDirtyPayload(row);
      try {
        await dataApi.update(payload, row.recordId);
        result.updated += 1;
        setRows(prev =>
          prev.map(r => {
            if (r.key !== row.key) return r;
            return {
              ...r,
              error: null,
              originalValues: { ...r.values },
            };
          })
        );
      } catch (e) {
        result.failed += 1;
        const data = getApiErrorResponseData(e);
        const msg =
          data?.message ||
          data?.error ||
          e?.message ||
          'Failed to update record';
        const label =
          row.recordLabel ||
          pickLabelFromRecord(null, nameFieldKey, row.values, row.displayValues) ||
          row.recordId;
        result.errors.push({
          id: row.recordId,
          label,
          error: msg,
        });
        setRows(prev =>
          prev.map(r => (r.key === row.key ? { ...r, error: msg } : r))
        );
      }
    }

    setSaveResult(result);
    setSaving(false);
  };

  /** Only lock into Close-only when every attempted save succeeded and nothing is still dirty. */
  const saveFinishedClean =
    !!saveResult &&
    (saveResult.failed || 0) === 0 &&
    dirtyRowCount === 0;

  const handleClose = () => {
    if (isBusy) return;
    const didComplete = !!saveResult && (saveResult.updated || 0) > 0;
    onClose({ didComplete, result: saveResult });
    onHide();
  };

  const renderInputForField = (row, fieldName) => {
    const meta =
      objectFieldsMap[fieldName] ||
      objectFieldsMap[String(fieldName).toLowerCase()] ||
      {};
    const dataType = String(meta.data_type || meta.type || 'text').toLowerCase();
    const value = row.values[fieldName] !== undefined ? row.values[fieldName] : '';
    const isRequired = !!(
      meta.is_required === true ||
      meta.isRequired === true ||
      meta.required === true
    );
    const requiredStyle = isRequired
      ? { ...CELL_INPUT_STYLE, ...REQUIRED_INPUT_STYLE }
      : CELL_INPUT_STYLE;
    const dirty = !valuesEqual(value, row.originalValues?.[fieldName]);
    const cellWrapStyle = dirty
      ? { background: 'rgba(1, 118, 211, 0.06)', borderRadius: 4, padding: 2 }
      : undefined;

    if (!isFieldEditable(fieldName)) {
      const displayText =
        row.displayValues?.[fieldName] ||
        (value == null || value === '' ? '—' : String(value));
      const typeHint =
        dataType === 'calculated'
          ? 'Calculated (read-only)'
          : dataType === 'rollupsummary'
            ? 'Rollup (read-only)'
            : dataType === 'autonumber'
              ? 'Auto-number (read-only)'
              : 'Read-only';
      return (
        <span
          className="text-muted small"
          title={typeHint}
          style={{
            display: 'inline-block',
            maxWidth: '100%',
            wordBreak: 'break-word',
            background: 'rgba(148, 163, 184, 0.12)',
            borderRadius: 4,
            padding: '4px 8px',
          }}
        >
          {displayText}
        </span>
      );
    }

    let control = null;

    if (dataType === 'textarea' || dataType === 'richtext') {
      control = (
        <Form.Control
          as="textarea"
          rows={2}
          value={value ?? ''}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        />
      );
    } else if (dataType === 'date') {
      control = (
        <Form.Control
          type="date"
          value={formatDateValue(value)}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        />
      );
    } else if (dataType === 'datetime' || dataType === 'datetime2') {
      control = (
        <Form.Control
          type="datetime-local"
          value={formatDateTimeValue(value)}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        />
      );
    } else if (dataType === 'time') {
      control = (
        <Form.Control
          type="time"
          value={formatTimeValue(value)}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        />
      );
    } else if (dataType === 'boolean' || dataType === 'checkbox') {
      control = (
        <div className="d-flex justify-content-center">
          <Form.Check
            type="checkbox"
            checked={normalizeBoolean(value)}
            onChange={e =>
              handleFieldChange(row.key, fieldName, e.target.checked)
            }
            disabled={saving}
          />
        </div>
      );
    } else if (dataType === 'picklist') {
      const options = getPicklistOptions(meta);
      control = (
        <Form.Select
          value={value ?? ''}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        >
          <option value="">-- Select --</option>
          {options.map(option => (
            <option key={option.key} value={option.key}>
              {option.value || option.key}
            </option>
          ))}
        </Form.Select>
      );
    } else if (dataType === 'multipicklist') {
      const picklistValues = getPicklistOptions(meta);
      const selectedValues =
        typeof value === 'string'
          ? value.split(';').filter(Boolean)
          : Array.isArray(value)
            ? value
            : [];
      control = (
        <MultiSelectDropdown
          selectedValues={selectedValues}
          picklistValues={picklistValues}
          onChange={selectedArray =>
            handleFieldChange(row.key, fieldName, selectedArray.join(';'))
          }
          isRequired={isRequired}
          placeholder="Select..."
          disabled={saving}
        />
      );
    } else if (
      [
        'number',
        'currency',
        'percent',
        'double',
        'int',
        'integer',
        'decimal',
        'float',
      ].includes(dataType)
    ) {
      control = (
        <Form.Control
          type="number"
          value={value ?? ''}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        />
      );
    } else if (dataType === 'lookup' || dataType === 'reference') {
      control = (
        <LookupLayout
          objectName={meta.reference_object || meta.referenceObject}
          searchFields={meta.lookup_search_fields || 'name'}
          displayFields={meta.display_fields || 'name'}
          value={value}
          defaultLabel={row.displayValues?.[fieldName] || ''}
          onChange={(id, label) =>
            handleLookupChange(row.key, fieldName, id, label)
          }
          isRequired={isRequired}
          disabled={saving}
          activeUsersOnly={isOwnerUserLookupField({
            fieldName,
            reference_object: meta.reference_object,
            referenceObject: meta.referenceObject,
            field_label: meta.field_label,
            label: meta.label,
          })}
        />
      );
    } else if (dataType === 'email') {
      control = (
        <Form.Control
          type="email"
          value={value ?? ''}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        />
      );
    } else if (dataType === 'phone') {
      control = (
        <Form.Control
          type="tel"
          value={value ?? ''}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        />
      );
    } else if (dataType === 'url') {
      control = (
        <Form.Control
          type="url"
          inputMode="url"
          value={value ?? ''}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        />
      );
    } else {
      control = (
        <Form.Control
          type="text"
          value={value ?? ''}
          onChange={e => handleFieldChange(row.key, fieldName, e.target.value)}
          style={requiredStyle}
          disabled={saving}
        />
      );
    }

    return <div style={cellWrapStyle}>{control}</div>;
  };

  const titleObject = objectLabel || objectName || 'Records';
  const recordCount =
    rows.length ||
    openSnapshotRef.current?.records?.length ||
    (selectedRecords || []).length ||
    0;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
      keyboard={!isBusy}
      className="bulk-edit-layout-modal"
      dialogClassName="bulk-edit-layout-dialog"
    >
      <Modal.Header closeButton={!isBusy}>
        <Modal.Title>
          Bulk Edit · {titleObject}{' '}
          <Badge bg="secondary" className="ms-2">
            {recordCount} record{recordCount === 1 ? '' : 's'}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ maxHeight: '70vh', overflow: 'auto', position: 'relative' }}
      >
        {saving && (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255,255,255,0.72)',
              zIndex: 10,
            }}
            role="status"
            aria-live="polite"
          >
            <Spinner animation="border" className="mb-2" />
            <span className="fw-semibold">Saving changes…</span>
          </div>
        )}

        {apiError && !saveResult && (
          <Alert variant="danger" className="mb-3">
            {apiError}
          </Alert>
        )}

        {saveResult && (
          <Alert
            variant={saveResult.failed > 0 ? 'warning' : 'success'}
            className="mb-3"
          >
            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
              <Badge bg="secondary">Total: {saveResult.total}</Badge>
              <Badge bg="success">Updated: {saveResult.updated}</Badge>
              <Badge bg="danger">Failed: {saveResult.failed}</Badge>
            </div>
            {Array.isArray(saveResult.errors) && saveResult.errors.length > 0 && (
              <ul className="mb-0 ps-3 small" style={{ maxHeight: 120, overflowY: 'auto' }}>
                {saveResult.errors.slice(0, 20).map((err, idx) => (
                  <li key={`${err.id}-${idx}`}>
                    <strong>{err.label || err.id}</strong>
                    {err.error ? ` — ${err.error}` : ''}
                  </li>
                ))}
              </ul>
            )}
            {saveResult.failed > 0 && (
              <div className="small mt-2 mb-0">
                Fix the values below and click <strong>Save</strong> again.
              </div>
            )}
          </Alert>
        )}

        {configLoading || !rowsReady ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <Spinner animation="border" role="status" className="mb-3" />
            <span>Loading records for bulk edit…</span>
          </div>
        ) : configError ? (
          <Alert variant="danger">{configError}</Alert>
        ) : activeFieldNames.length === 0 ? (
          <Alert variant="info">
            No editable list-view fields available for bulk edit. Calculated,
            system, and read-only fields are excluded.
          </Alert>
        ) : rows.length === 0 ? (
          <Alert variant="warning">No selected records to edit.</Alert>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="text-muted small">
                Editing list-view columns only. Each changed row is saved as its
                own record update. Owner changes use <strong>Change Owner</strong>.
              </div>
              <Badge bg={dirtyRowCount > 0 ? 'primary' : 'light'} text={dirtyRowCount > 0 ? undefined : 'dark'}>
                {dirtyRowCount} changed row{dirtyRowCount === 1 ? '' : 's'}
              </Badge>
            </div>

            <Table bordered responsive hover size="sm" className="align-middle mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: 48, minWidth: 48 }}>#</th>
                  {activeFieldNames.map(fieldName => {
                    const meta =
                      objectFieldsMap[fieldName] ||
                      objectFieldsMap[String(fieldName).toLowerCase()] ||
                      {};
                    const label = meta.field_label || meta.label || fieldName;
                    const required = !!(
                      meta.is_required === true ||
                      meta.isRequired === true ||
                      meta.required === true
                    );
                    return (
                      <th key={fieldName} style={{ minWidth: 160, whiteSpace: 'nowrap' }}>
                        <span className="d-flex align-items-center">
                          {required && (
                            <span style={REQUIRED_ASTERISK_STYLE} aria-hidden>
                              *
                            </span>
                          )}
                          {label}
                        </span>
                      </th>
                    );
                  })}
                  <th style={{ width: 72 }}>Reset</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={row.key}
                    className={row.error ? 'table-danger' : undefined}
                  >
                    <td className="text-center small text-muted">{index + 1}</td>
                    {activeFieldNames.map(fieldName => (
                      <td key={`${row.key}-${fieldName}`}>
                        {renderInputForField(row, fieldName)}
                      </td>
                    ))}
                    <td className="text-center">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`reset-${row.key}`}>Reset row</Tooltip>
                        }
                      >
                        <span>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleResetRow(row.key)}
                            disabled={isBusy}
                          >
                            <FaUndo />
                          </Button>
                        </span>
                      </OverlayTrigger>
                      {row.error && (
                        <div className="small text-danger mt-1" title={row.error}>
                          Error
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {saveFinishedClean ? (
          <Button variant="primary" onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={handleClose} disabled={isBusy}>
              {saveResult ? 'Close' : 'Cancel'}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={
                isBusy ||
                (dirtyRowCount === 0 && !(saveResult?.failed > 0))
              }
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                <>
                  Save{' '}
                  {dirtyRowCount > 0
                    ? dirtyRowCount
                    : saveResult?.failed > 0
                      ? saveResult.failed
                      : ''}{' '}
                  Change
                  {(dirtyRowCount || saveResult?.failed || 0) === 1 ? '' : 's'}
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
